import type * as THREE from 'three';
import {SketchTrimMode, type SketchTrimEntityRef} from '../SketchTrimTypes';
import type {SketchTrimController} from '../SketchTrimController';
import type {SketchTrimModeService} from './SketchTrimModeServiceTypes';

/**
 * Inside/Outside 双边界选择模式公共服务。
 *
 * 职责：
 * 1. 统一维护“双边界 + 开放目标”与“单闭合边界 + 开放目标”的顺序选择状态；
 * 2. 按当前选择阶段切换边界与目标的实体准入规则；
 * 3. 把确定的两实体或三实体操作委托给剪裁控制器，禁止两种模式复制同一套状态机。
 */
export class SketchTrimInsideOutsideModeService implements SketchTrimModeService {
    /** 当前服务绑定的剪裁模式。 */
    public readonly mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside;

    /** 第一条边界。 */
    protected boundaryA: SketchTrimEntityRef | null = null;

    /** 第二条边界。 */
    protected boundaryB: SketchTrimEntityRef | null = null;

    /** 第一条边界是否自身构成完整闭合区域。 */
    private boundaryAIsClosed = false;

    /** 所属剪裁控制器。 */
    private readonly controller: SketchTrimController;

    /** 属性面板显示文案。 */
    private readonly displayName: string;

    /**
     * 构造公共双边界模式服务。
     *
     * @param controller 剪裁控制器。
     * @param mode Inside 或 Outside 模式。
     * @param displayName 属性面板显示文案。
     */
    public constructor(
        controller: SketchTrimController,
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        displayName: string,
    ) {
        this.controller = controller;
        this.mode = mode;
        this.displayName = displayName;
    }

    /**
     * 获取模式显示文案。
     *
     * @returns 当前模式文案。
     */
    public getDisplayName(): string {
        return this.displayName;
    }

    /**
     * 重置双边界选择状态。
     */
    public resetState(): void {
        this.boundaryA = null;
        this.boundaryB = null;
        this.boundaryAIsClosed = false;
        this.controller.serviceSetLastSelectedEntity(null);
    }

    /**
     * 判断当前选择阶段是否允许选择指定实体。
     *
     * 规则：
     * 1. 第一项允许全部受支持草图曲线作为边界；
     * 2. 第一项开放时，第二项继续选择边界，第三项只允许开放目标；
     * 3. 第一项闭合时，第二项直接只允许开放目标；
     * 4. 已选边界始终保持可选，使再次点击能够取消选择。
     *
     * @param object 待判断实体。
     * @returns 当前阶段允许选择时返回 true。
     */
    public isSelectableEntity(object: THREE.Object3D): boolean {
        if (
            object.uuid === this.boundaryA?.objectId
            || object.uuid === this.boundaryB?.objectId
        ) {
            return true;
        }

        if (!this.boundaryA) {
            return this.controller.serviceIsInsideOutsideBoundaryObject(object);
        }

        // 单闭合边界已经完整定义内外区域，第二次选择必须直接限制为开放目标。
        if (this.boundaryAIsClosed) {
            return this.controller.serviceIsInsideOutsideTargetObject(object);
        }

        if (!this.boundaryB) {
            return this.controller.serviceIsInsideOutsideBoundaryObject(object);
        }

        return this.controller.serviceIsInsideOutsideTargetObject(object);
    }

    /**
     * 处理选择管理器结算。
     */
    public handleSelectionUpdated(): void {
        const selectionManager = this.controller.getViewer().entitySelectionManager;
        if (!selectionManager) {
            return;
        }

        // 选择列表必须保留已确定的闭合边界，同时过滤其它不属于当前模式的对象。
        const selectedPicks = selectionManager.getSelectedPicks()
            .filter((pick) => this.isSelectableEntity(pick.object));
        if (selectedPicks.length <= 0) {
            this.resetState();
            return;
        }

        const firstPick = selectedPicks[0]!;
        const nextBoundaryA = this.controller.serviceCreateTrimEntityRefFromIntersection(firstPick);
        const nextBoundaryAIsClosed = this.controller.serviceIsInsideOutsideClosedBoundaryObject(firstPick.object);
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(nextBoundaryA));
        if (selectedPicks.length === 1) {
            const boundaryChanged = !this.boundaryA
                || !this.controller.serviceIsSameTrimEntity(this.boundaryA, nextBoundaryA);
            this.boundaryA = nextBoundaryA;
            this.boundaryB = null;
            this.boundaryAIsClosed = nextBoundaryAIsClosed;
            if (boundaryChanged) {
                this.controller.getViewer().drawingNoticeCallback?.(
                    nextBoundaryAIsClosed
                        ? '已选择闭合边界，请选择要裁剪的开放草图实体。'
                        : '已选择第一条边界，请选择第二条边界。',
                    'info',
                );
            }
            return;
        }

        if (nextBoundaryAIsClosed) {
            this.handleClosedBoundaryTargetSelection(
                nextBoundaryA,
                selectedPicks[selectedPicks.length - 1]!,
            );
            return;
        }

        const nextBoundaryB = this.controller.serviceCreateTrimEntityRefFromIntersection(selectedPicks[1]!);
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(nextBoundaryB));
        if (selectedPicks.length === 2) {
            const boundariesChanged = !this.boundaryA
                || !this.boundaryB
                || !this.controller.serviceIsSameTrimEntity(this.boundaryA, nextBoundaryA)
                || !this.controller.serviceIsSameTrimEntity(this.boundaryB, nextBoundaryB);
            this.boundaryA = nextBoundaryA;
            this.boundaryB = nextBoundaryB;
            this.boundaryAIsClosed = false;
            if (boundariesChanged) {
                this.controller.getViewer().drawingNoticeCallback?.('已选择第二条边界，请选择要裁剪的开放草图实体。', 'info');
            }
            return;
        }

        // 每次执行前都以选择管理器中的前两项重建边界，确保用户取消并重选后不会使用过期引用。
        this.boundaryA = nextBoundaryA;
        this.boundaryB = nextBoundaryB;
        this.boundaryAIsClosed = false;
        const targetEntity = this.controller.serviceCreateTrimEntityRefFromIntersection(
            selectedPicks[selectedPicks.length - 1]!,
        );
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(targetEntity));
        const result = this.controller.serviceApplyInsideOutsideOperation(
            this.mode,
            this.boundaryA,
            this.boundaryB,
            targetEntity,
        );
        if (result.success) {
            this.boundaryA = null;
            this.boundaryB = null;
            this.controller.serviceClearTrimSelectionFromSelectionManager();
        }
        this.controller.servicePublishResult(result);
    }

    /**
     * 消费“单闭合边界 + 开放目标”的第二次选择。
     *
     * @param boundaryEntity 第一项闭合边界。
     * @param targetPick 第二项开放目标命中。
     */
    private handleClosedBoundaryTargetSelection(
        boundaryEntity: SketchTrimEntityRef,
        targetPick: THREE.Intersection<THREE.Object3D>,
    ): void {
        this.boundaryA = boundaryEntity;
        this.boundaryB = null;
        this.boundaryAIsClosed = true;

        const targetEntity = this.controller.serviceCreateTrimEntityRefFromIntersection(targetPick);
        this.controller.serviceSetLastSelectedEntity(this.controller.serviceCloneTrimEntityRef(targetEntity));
        const result = this.controller.serviceApplyClosedBoundaryInsideOutsideOperation(
            this.mode,
            boundaryEntity,
            targetEntity,
        );
        if (result.success) {
            this.resetState();
            this.controller.serviceClearTrimSelectionFromSelectionManager();
        } else {
            // 失败目标不能继续占据第二项，否则下一次点击会形成陈旧目标堆积。
            this.controller.serviceRetainInsideOutsideBoundarySelection([boundaryEntity]);
        }
        this.controller.servicePublishResult(result);
    }
}
